/* eslint-disable max-lines */
import { action, observable, computed, transaction } from 'mobx';
import { StatusCodes } from 'http-status-codes';

import { formatMessageDate } from 'utils/date/formatUnixDate';

import firebaseNode from '../../../../firebase/firebaseNode';
import { FirebaseMain } from '../../../../firebase/firebase';

import {
  API_STATES,
  CONVERSATION_TAB_VALUES,
  getActiveConversationEmptyStructure,
  getActiveConversationPreloadDetails,
  getMediaMessage,
  getMessagePostParams,
  getPreviewFromConversation,
  validateFile,
} from './utils';
import {
  deleteConv,
  fetchConversation,
  fetchPreviews,
  markAsRead,
  postBanUser,
  postDeleteMessage,
  postFile,
  postMarkAsSpam,
  postMessage,
  postReportAbuse,
  putConversation,
} from './Api';


class MessengerStore {
  @observable previews = [];
  @observable previewsLoadingState = API_STATES.INITIAL;

  @observable activeConversationTab = CONVERSATION_TAB_VALUES.CHAT;

  @observable activeConversation = getActiveConversationEmptyStructure();
  @observable activeConversationPreloadDetails = {};
  @observable activeConversationLoadingState = API_STATES.INITIAL;
  @observable activeConversationLoadingMoreState = API_STATES.INITIAL;

  @observable activeConversationMedia = [];
  @observable activeConversationMediaLoadingState = API_STATES.INITIAL;
  @observable activeConversationMediaLoadingMoreState = API_STATES.INITIAL;

  @observable activeConversationSavedMessages = [];
  @observable activeConversationSavedMessagesLoadingState = API_STATES.INITIAL;
  @observable activeConversationSavedMessagesLoadingMoreState = API_STATES.INITIAL;

  @observable deleteMessageLoadingState = API_STATES.INITIAL;
  @observable markAsSpamLoadingState = API_STATES.INITIAL;
  @observable reportAbuseLoadingState = API_STATES.INITIAL;
  @observable reportAbuseErrorMessage = '';

  @observable createConversationLoadingState = API_STATES.INITIAL;
  @observable deleteConversationLoadingState = API_STATES.INITIAL;
  @observable deleteMultipleConversationsLoadingState = API_STATES.INITIAL;

  @observable banUserLoadingState = API_STATES.INITIAL;

  @observable uploads = [];

  @computed get activeConversationThreadId() {
    return this.activeConversation?.threadId;
  }

  @computed get activeConversationMessages() {
    return this.activeConversation?.messages;
  }

  @computed get activeConversationReceiver() {
    return this.activeConversation?.receiver;
  }

  @computed get activeConversationIsMassAdminThread() {
    return this.activeConversation?.isMassAdminThread;
  }

  @computed get activeConversationCanLoadMore() {
    return this.activeConversation?.canLoadMore;
  }

  @action setActiveConversationTab = (newValue) => {
    this.activeConversationTab = newValue;
  };

  @action resetActiveConversationTab = () => {
    this.activeConversationTab = CONVERSATION_TAB_VALUES.CHAT;
  };

  @action loadPreviews = async(filter) => {
    this.previewsLoadingState = API_STATES.LOADING;

    const { result, error } = await fetchPreviews(filter);
    if (!result || error) {
      this.previewsLoadingState = API_STATES.ERROR;
    }

    this.previewsLoadingState = API_STATES.INITIAL;
    this.previews = observable(result);
  };

  @action _loadConversation = async(threadId) => {
    this.activeConversationLoadingState = API_STATES.LOADING;

    const preloadDetails = getActiveConversationPreloadDetails(this.previews, threadId);
    this.activeConversationPreloadDetails = observable(preloadDetails);
    this.activeConversationTab = CONVERSATION_TAB_VALUES.CHAT;

    const { result, error } = await fetchConversation(threadId);
    if (!result || error) {
      this.activeConversationLoadingState = API_STATES.ERROR;

      return null;
    }

    this.activeConversationLoadingState = API_STATES.INITIAL;
    this.activeConversation = observable(result);

    return result;
  };

  @action _markAsRead = async(threadId) => {
    const { result, error } = await markAsRead(threadId);
    if (!result || error) {
      return;
    }

    this.previews = this.previews.map((preview) => {
      if (preview.threadId === threadId) {
        return { ...preview, unreadMsgCount: 0 };
      }

      return preview;
    });
  };

  @action loadConversation = async(threadId) => {
    const result = await this._loadConversation(threadId);
    if (!result) {
      return;
    }

    const isSameConversationOpen = this.activeConversation?.threadId === threadId;
    if (!isSameConversationOpen) {
      return;
    }

    const thread = this.previews.find((item) => item.threadId === threadId);
    if (!thread?.unreadMsgCount) {
      return;
    }

    await this._markAsRead(threadId);
  };

  @action createConversation = async(receiverName) => {
    this.createConversationLoadingState = API_STATES.LOADING;

    const {
      result: putConversationResult,
      error: putConversationError,
    } = await putConversation(receiverName);

    if (!putConversationResult || putConversationError) {
      this.createConversationLoadingState = API_STATES.ERROR;
      // must add error state, and dispatch properly

      return;
    }

    const newThreadId = putConversationResult;
    const {
      result: fetchConversationResult,
      error: fetchConversationError,
    } = await fetchConversation(newThreadId);

    if (!fetchConversationResult || fetchConversationError) {
      this.createConversationLoadingState = API_STATES.ERROR;
      // must add error state, and dispatch properly

      return;
    }

    const newConversation = fetchConversationResult;
    this.activeConversation = observable(newConversation);
    this.activeConversationLoadingState = API_STATES.INITIAL;
    this.activeConversationPreloadDetails = observable({});

    const newPreview = {
      isMassAdminThread: newConversation.isMassAdminThread,
      lastMessageReadId: newConversation.lastReadMsgId,
      lastPerformerMassMessageReadId: 0,
      preview: {},
      read: false,
      receiverName,
      threadId: newThreadId,
      unreadMsgCount: 0,
      users: newConversation.users,
    };
    this.previews.push(newPreview);

    this.createConversationLoadingState = API_STATES.SUCCESS;
  };

  @action resetCreateConversationState = () => {
    this.createConversationLoadingState = API_STATES.INITIAL;
  };

  @action unsetActiveConversation = () => {
    this.activeConversation = observable(getActiveConversationEmptyStructure());
  };

  @action deleteConversation = async(threadId) => {
    this.deleteConversationLoadingState = API_STATES.LOADING;

    const { result, error } = await deleteConv(threadId);
    if (error || result.status !== 'success') {
      this.deleteConversationLoadingState = API_STATES.ERROR;

      return;
    }

    this.previews = this.previews.filter((item) => item.threadId !== threadId);

    this.deleteConversationLoadingState = API_STATES.SUCCESS;
  };

  @action resetDeleteConversationLoadingState = () => {
    this.deleteConversationLoadingState = API_STATES.INITIAL;
  };

  @action deleteMultipleConversations = async(valuesArray) => {
    this.deleteMultipleConversationsLoadingState = API_STATES.LOADING;

    // requires new BE endpoint
    // const { result, error } = await deleteMultipleConv(valuesArray);

    await new Promise((resolve) => setTimeout(resolve, 0));

    const result = true;
    const error = false;
    if (error || !result) {
      this.deleteMultipleConversationsLoadingState = API_STATES.ERROR;

      return;
    }

    this.previews = this.previews.filter(
      (item) => !valuesArray.includes(item.threadId)
    );

    if (valuesArray.includes(this.activeConversationThreadId)) {
      this.unsetActiveConversation();
    }

    this.deleteMultipleConversationsLoadingState = API_STATES.INITIAL;
  };

  @action resetDeleteMultipleConversationsLoadingState = () => {
    this.deleteConversationLoadingState = API_STATES.INITIAL;
  };

  @action loadMoreActiveConversationMessages = async({ threadId, offset }) => {
    this.activeConversationLoadingMoreState = API_STATES.LOADING;

    const { result, error } = await fetchConversation(threadId, offset);

    const isSameConversationOpen = this.activeConversation?.threadId === threadId;
    if (!isSameConversationOpen) {
      return;
    }

    if (!result || error) {
      this.activeConversationLoadingMoreState = API_STATES.ERROR;

      return;
    }

    transaction(() => {
      this.activeConversation.canLoadMore = result.canLoadMore;
      this.activeConversation.messages = [
        ...this.activeConversation.messages,
        ...result.messages,
      ];
      this.activeConversationLoadingMoreState = API_STATES.INITIAL;
    });
  };

  @action loadConversationMedia = async(threadId) => {
    this.activeConversationMediaLoadingState = API_STATES.LOADING;

    // requires new BE endpoint
    await new Promise((resolve) => setTimeout(resolve, 0));

    const result = [];
    const error = false;
    if (!result || error) {
      this.activeConversationMediaLoadingState = API_STATES.ERROR;

      return;
    }

    const isSameConversationOpen = this.activeConversation?.threadId === threadId;
    if (!isSameConversationOpen) {
      return;
    }

    this.activeConversationMedia = [
      ...this.activeConversationMedia,
      ...result,
    ];

    this.activeConversationMediaLoadingState = API_STATES.INITIAL;
  };

  @action loadMoreConversationMedia = async({ threadId }) => {
    this.activeConversationMediaLoadingMoreState = API_STATES.LOADING;

    // requires new BE endpoint
    await new Promise((resolve) => setTimeout(resolve, 0));

    const result = [];
    const error = false;

    const isSameConversationOpen = this.activeConversation?.threadId === threadId;
    if (!isSameConversationOpen) {
      return;
    }

    if (!result || error) {
      this.activeConversationMediaLoadingMoreState = API_STATES.ERROR;

      return;
    }

    transaction(() => {
      this.activeConversationMedia = [
        ...this.activeConversationMedia,
        ...result,
      ];
      this.activeConversationMediaLoadingMoreState = API_STATES.INITIAL;
    });
  };

  @action loadConversationSavedMessages = async(threadId) => {
    this.activeConversationSavedMessagesLoadingState = API_STATES.LOADING;

    // requires new BE endpoint
    await new Promise((resolve) => setTimeout(resolve, 0));

    const result = [];
    const error = false;
    if (!result || error) {
      this.activeConversationSavedMessagesLoadingState = API_STATES.ERROR;

      return;
    }

    const isSameConversationOpen = this.activeConversation?.threadId === threadId;
    if (!isSameConversationOpen) {
      return;
    }

    this.activeConversationSavedMessages = [
      ...this.activeConversationSavedMessages,
      ...result,
    ];

    this.activeConversationSavedMessagesLoadingState = API_STATES.INITIAL;
  };

  @action loadMoreConversationSavedMessages = async({ threadId }) => {
    this.activeConversationSavedMessagesLoadingMoreState = API_STATES.LOADING;

    // requires new BE endpoint
    await new Promise((resolve) => setTimeout(resolve, 0));

    const result = [];
    const error = false;

    const isSameConversationOpen = this.activeConversation?.threadId === threadId;
    if (!isSameConversationOpen) {
      return;
    }

    if (!result || error) {
      this.activeConversationSavedMessagesLoadingMoreState = API_STATES.ERROR;

      return;
    }

    transaction(() => {
      this.activeConversationSavedMessages = [
        ...this.activeConversationSavedMessages,
        ...result,
      ];
      this.activeConversationSavedMessagesLoadingMoreState = API_STATES.INITIAL;
    });
  };

  @action sendMessage = async({ message, threadId }) => {
    const params = getMessagePostParams({ message, threadId });

    const { result, error } = await postMessage(params);

    if (!result || error) {
      // must add error state, and dispatch properly
      // this requires an entirely new improvement flow
    }
  };

  @action deleteMessage = async({ threadId, messageId }) => {
    this.deleteMessageLoadingState = API_STATES.LOADING;

    const { error } = await postDeleteMessage({ threadId, messageId });
    if (error) {
      this.deleteMessageLoadingState = API_STATES.ERROR;

      return;
    }

    this.activeConversation.messages = this.activeConversation.messages.filter(
      (message) => message.msgId !== messageId
    );

    this.deleteMessageLoadingState = API_STATES.INITIAL;
  };

  @action resetDeleteMessageState = () => {
    this.deleteMessageLoadingState = API_STATES.INITIAL;
  };

  @action markAsSpam = async({ threadId, messageId }) => {
    this.markAsSpamLoadingState = API_STATES.LOADING;

    const { error } = await postMarkAsSpam({ threadId, messageId });
    if (error) {
      this.markAsSpamLoadingState = API_STATES.ERROR;

      return;
    }

    this.markAsSpamLoadingState = API_STATES.SUCCESS;
  };

  @action resetMarkAsSpamState = () => {
    this.markAsSpamLoadingState = API_STATES.INITIAL;
  };

  @action reportAbuse = async(data) => {
    this.reportAbuseLoadingState = API_STATES.LOADING;

    const errorMessages = [
      'You have to wait 60 seconds between 2 abuse reports',
      'Sorry, you may only report someone once per day. Your previous report has been received and will be processed.',
    ];

    // this returns 200 even when "error"
    const { result, error } = await postReportAbuse(data);
    if (!result || error || errorMessages.includes(result)) {
      this.reportAbuseLoadingState = API_STATES.ERROR;
      this.reportAbuseErrorMessage = result || '';

      return;
    }

    this.reportAbuseLoadingState = API_STATES.SUCCESS;
  };

  @action resetReportAbuseState = () => {
    this.reportAbuseLoadingState = API_STATES.INITIAL;
  };

  @action banUser = async(threadId, receiverName) => {
    this.banUserLoadingState = API_STATES.LOADING;

    const { error } = await postBanUser(receiverName);

    if (error) {
      this.banUserLoadingState = API_STATES.ERROR;

      return;
    }

    this.previews = this.previews.filter((item) => item.threadId !== threadId);

    this.banUserLoadingState = API_STATES.SUCCESS;
  };

  @action resetBanUserLoadingState = () => {
    this.banUserLoadingState = API_STATES.INITIAL;
  };

  @action onMessageReceived = (user) => async(snapshot) => {
    const message = snapshot.val();

    const {
      msgId,
      sendDate,
      senderId,
      threadId,
    } = message || {};

    if (!message || !msgId || !senderId) {
      return;
    }

    const userId = user?.userId;
    const isGroupConversation = threadId === -1 * userId;
    if (isGroupConversation) {
      // based on the re-re-re-discussed specs, this must be re-implemented

      return;
    }

    message.sendDate = formatMessageDate(sendDate);
    const existingPreview = this.previews.find((item) => item.threadId === threadId);
    if (existingPreview) {
      existingPreview.preview = {
        ...existingPreview.preview,
        ...message,
      };
    }

    const activeConversationThreadId = this.activeConversation?.threadId;
    const isActiveConversation = (threadId === activeConversationThreadId);
    if (isActiveConversation) {
      this.previews = this.previews.filter((item) => item.threadId !== threadId);
      this.previews = [existingPreview, ...this.previews];

      this.activeConversation.messages.unshift(message);

      if (this.activeConversationTab === CONVERSATION_TAB_VALUES.CHAT) {
        this._markAsRead(activeConversationThreadId);
      }

      return;
    }

    if (existingPreview) {
      const isSentByUser = userId === senderId;
      if (!isSentByUser) {
        existingPreview.unreadMsgCount += 1;
      }

      this.previews = this.previews.filter((item) => item.threadId !== threadId);
      this.previews = [existingPreview, ...this.previews];

      return;
    }

    const { result } = await fetchConversation(threadId);
    // must handle error

    const generatedNewPreview = getPreviewFromConversation(message, result);
    this.previews.push(generatedNewPreview);
  };

  watchConversationState = (user) => {
    const { username } = user;

    const conversationStateRef =
      FirebaseMain
        .child(firebaseNode.USER_NOTIFICATION)
        .child(username.toLowerCase());

    conversationStateRef.on('value', this.onMessageReceived(user));
  };

  @action uploadFile = ({ file, threadId }) => {
    const fileValidation = validateFile(file);

    const { format, previewSrc } = fileValidation;
    const msgId = Date.now();
    const mType = format;

    let accessUrl = undefined;
    let tmpUploadUrl = undefined;

    const _addFileUploadProgress = (params) => {
      this.uploads.push(params);
    };

    const _updateFileUploadProgress = (progress) => {
      const index = this.uploads.findIndex((item) => item.msgId === msgId);
      if (index < 0) {
        return;
      }

      this.uploads[index].progress = progress;
    };

    const _removeFileUploadProgress = () => {
      const index = this.uploads.findIndex((item) => item.msgId === msgId);
      if (index < 0) {
        return;
      }

      this.uploads = this.uploads.filter((item) => item.msgId !== msgId);
    };

    const _setFileError = (errors) => {
      const index = this.uploads.findIndex((item) => item.msgId === msgId);
      if (index < 0) {
        return;
      }

      const updated = {
        ...this.uploads[index],
        progress: 0,
        ...errors,
      };

      this.uploads[index].progress = updated;
    };

    const _sendMessage = async() => {
      const formattedMessage = getMediaMessage({ mType, accessUrl, threadId });
      const {
        result: postMessageResult,
        error: postMessageError,
      } = await postMessage(formattedMessage);

      if (!postMessageResult || postMessageError) {
        _setFileError({
          uploadError: false,
          sendError: true,
        });

        return;
      }

      _removeFileUploadProgress();
    };

    const _uploadFile = async() => {
      const { result, error } = await postFile(file);

      if (!result || error) {
        // must add proper error checks

        return;
      }

      const {
        accessUrl: responseAccessUrl,
        tmpUploadUrl: responseTmpUploadUrl,
      } = result;

      accessUrl = responseAccessUrl;
      tmpUploadUrl = responseTmpUploadUrl;

      const xhr = new XMLHttpRequest();
      xhr.open('PUT', tmpUploadUrl, true);

      xhr.upload.onprogress = function({ loaded, total }) {
        const progress = parseInt(Math.round((loaded * 100) / total), 10);
        _updateFileUploadProgress(progress);
      };

      xhr.onabort = function() {
        _removeFileUploadProgress();
      };

      xhr.onload = function() {
        if (xhr.readyState === 4 && xhr.status === StatusCodes.CREATED) {
          _sendMessage();
        }
      };

      xhr.onerror = function() {
        _setFileError({
          uploadError: true,
          sendError: false,
        });
      };

      _addFileUploadProgress({
        mType: format,
        previewSrc,
        progress: 0,
        sendError: false,
        uploadError: false,
        onAbort: () => xhr.abort(),
        onRetryUpload: _uploadFile,
        onRetrySend: _sendMessage,
      });

      xhr.send(file);
    };

    _uploadFile();
  };
}

export default new MessengerStore();
